// frontend/src/contexts/AuthContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Determine the API base URL based on environment
const getBaseURL = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8000';
  }
  return `${window.location.protocol}//${window.location.host}`;
};

// Create axios instance with proper configuration
const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Function to get CSRF token from cookies
const getCSRFToken = () => {
  const name = 'gopak_csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

// Add CSRF token to requests
api.interceptors.request.use((config) => {
  const token = getCSRFToken();
  if (token) {
    config.headers['X-CSRFToken'] = token;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [allowedPages, setAllowedPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/api/auth/user/');
      setUser(response.data);

      // Fetch permissions after getting user
      await fetchUserPermissions();
    } catch (error) {
      // Only clear user on actual auth failures (401/403)
      // Network errors or server errors should NOT log the user out
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('User not authenticated');
        setUser(null);
        setAllowedPages([]);
      } else {
        console.warn('Auth check failed (network/server error) - keeping current session', error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPermissions = async () => {
    try {
      const response = await api.get('/api/auth/permissions/');
      console.log('Permissions fetched:', response.data);
      console.log('Allowed pages:', response.data.allowed_pages);
      console.log('Is admin:', response.data.is_admin);
      
      // Ensure profile and dashboard are always included
      const pages = response.data.allowed_pages || [];
      const defaultPages = ['profile', 'dashboard'];
      const allPages = [...new Set([...pages, ...defaultPages])];
      
      setAllowedPages(allPages);
      return allPages;
    } catch (error) {
      console.error('❌ Error fetching permissions:', error);
      // Even on error, give them profile and dashboard
      setAllowedPages(['profile', 'dashboard']);
      return ['profile', 'dashboard'];
    }
  };

  const login = async (username, password) => {
    try {
      // Get CSRF token first
      await api.get('/api/auth/csrf/');
      
      const response = await api.post('/api/auth/login/', {
        username,
        password
      });
      
      if (response.data.success) {
        setUser(response.data.user);
        
        // Fetch permissions after login
        await fetchUserPermissions();
        
        return { success: true };
      }
      
      return { success: false, error: 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed. Please check your credentials.' 
      };
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setAllowedPages([]);
    }
  };

  const hasPageAccess = (pageKey) => {
    // Admins have access to everything
    if (user?.is_staff) return true;
    
    // Profile and dashboard are always accessible
    if (pageKey === 'profile' || pageKey === 'dashboard') return true;
    
    // Check if page is in allowed pages
    return allowedPages.includes(pageKey);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    allowedPages,
    hasPageAccess,
    api // Expose the configured axios instance
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};